import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs, or,
  query,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { Organization } from "../models/organization.model";
import { ObjectHelper } from "../helpers/object.helper";
import { BehaviorSubject } from "rxjs";
import { Router } from "@angular/router";
import { Functions, httpsCallable, HttpsCallableResult } from "@angular/fire/functions";
import { LegalData } from "../models/legal-data.model";
import { Subcontractor } from "../models/subcontractor.model";
import { SupplierInvitation } from "../models/supplier-invitation.model";
import { PluginEnum } from "../enums/plugin.enum";

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {

  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private router = inject(Router);

  organization$ = new BehaviorSubject<Organization>(null);

  /**
   * Wait until the organisation is loaded
   */
  async waitOrganization(): Promise<void> {
    while (!this.organization$.value) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  get currentOrganization(): Organization {
    return this.organization$.value;
  }

  set currentOrganization(organization: Organization) {
    if (!organization) {
      this.router.navigate(['/']).catch(err => console.error(err));
    }

    if (JSON.stringify(organization) === JSON.stringify(this.organization$.value)) {
      return;
    }

    this.organization$.next(organization ?? new Organization());
  }

  /**
   * Create new organisation
   * @param organization The org
   * @returns The doc ref
   */
  create(organization: Partial<Organization>): Promise<DocumentReference<Organization>> {
    const ref = collection(this.firestore, 'organizations')
      .withConverter(Organization.converter);
    return addDoc(ref, new Organization(organization));
  }

  /**
   * Check id organisation id is available
   * @param organizationId the org id
   * @returns a bool
   */
  async orgIdIsAvailable(organizationId: string): Promise<boolean> {
    const q = query(collection(this.firestore, 'organizations'),
      where('oid', '==', organizationId))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * List organizations of a user
   * @param uid The user id
   * @returns The organizations
   */
  async listAdminOrganizations(uid: string): Promise<Organization[]> {
    const q = query(collection(this.firestore, 'organizations'),
      where('administrators', 'array-contains', uid))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty ? [] : snapshot.docs.map(doc => doc.data());
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * List organizations of a user
   * @param uid The user id
   * @returns The organizations
   */
  async listUserOrganizations(uid: string): Promise<Organization[]> {
    const q = query(collection(this.firestore, 'organizations'),
      where('users', 'array-contains', uid))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty ? [] : snapshot.docs.map(doc => doc.data())
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * List supplier of companies
   * @param orgIds The user id
   * @returns The organizations
   */
  async listSupplierOrganizations(orgIds: string[]): Promise<Organization[]> {
    if (!orgIds.length) {
      return [];
    }
    const q = query(collection(this.firestore, 'organizations'),
      or(
        ...orgIds.map(id => where('supplierOfOrgIds', 'array-contains', id))
      ))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty ? [] : snapshot.docs.map(doc => doc.data())
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * List supplier invitation of companies
   * @param orgId The organization id that has send the invitation
   * @returns The organizations
   */
  async listSupplierInvitations(orgId: string): Promise<SupplierInvitation[]> {
    const q = query(collection(this.firestore, 'invitation-supplier'),
      where('fromOrganizationId', '==', orgId)
    )
      .withConverter(SupplierInvitation.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty ? [] : snapshot.docs.map(doc => doc.data())
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Get an organization by id
   * @param id The organization id
   * @returns The org
   */
  async get(id: string): Promise<Organization> {
    const ref = doc(this.firestore, 'organizations', id)
      .withConverter(Organization.converter);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return null;
    }
    return docSnap.data();
  }


  /**
   * Get an organization by oid
   * @param organizationId The organization id
   * @returns The org
   */
  async getByOid(organizationId: string): Promise<Organization> {
    const q = query(collection(this.firestore, 'organizations'),
      where('oid', '==', organizationId))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        const organization = snapshot.docs[0].data();
        organization.id = snapshot.docs[0].id;
        return snapshot.docs[0].data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Get an organization by siret
   * @param siret The organization siret
   * @returns The org
   */
  async getBySiret(siret: string): Promise<Organization> {
    const q = query(collection(this.firestore, 'organizations'),
      where('siret', '==', siret))
      .withConverter(Organization.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        const organization = snapshot.docs[0].data();
        organization.id = snapshot.docs[0].id;
        return snapshot.docs[0].data();
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Update an organization
   * @param id The organization id
   * @param organization The partial organization object
   * @returns The promise
   */
  async update(id: string, organization: Partial<Organization>): Promise<void> {
    const ref = doc(this.firestore, 'organizations', id).withConverter(Organization.converter);
    await updateDoc(ref, ObjectHelper.anonymize(organization));
    this.currentOrganization = await this.get(id);
  }

  /**
   * Fetches data from a remote service using the given SIRET number.
   * @param siret - The SIRET number used to query the service.
   * @returns A promise that resolves with the result containing the SIRET data.
   */
  getDataFromSiret(siret: string): Promise<HttpsCallableResult<LegalData>> {
    return httpsCallable<{ siret: string }, LegalData>(
      this.functions, 'siret_data_gen2')(
      { siret })
  }

  /**
   * Fetches data from a remote service using the given SIRET number.
   * @param orgId - The organization id
   * @param supplierOfOrgIds - The ids of clients to add
   * @returns A promise that resolves with the result containing the SIRET data.
   */
  addClient(orgId: string, supplierOfOrgIds: string[]): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ orgId: string, supplierOfOrgIds: string[] }, void>(
      this.functions, 'add_client_gen2')({ orgId, supplierOfOrgIds: supplierOfOrgIds })
  }
  /**
   * Fetches data from a remote service using the given SIRET number.
   * @param orgId - The organization id
   * @param supplierOfOrgIds - The ids of clients to add
   * @returns A promise that resolves with the result containing the SIRET data.
   */
  removeClient(orgId: string, supplierOfOrgIds: string[]): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ orgId: string, supplierOfOrgIds: string[] }, void>(
      this.functions, 'remove_client_gen2')({ orgId, supplierOfOrgIds: supplierOfOrgIds })
  }

  /**
   * Send the mail to the supplier invitation
   * @param invitationId - The invitation id
   * @returns A promise that resolves with the result containing the SIRET data.
   */
  sendSupplierInvitation(invitationId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ invitationId: string }, void>(
      this.functions, 'send_supplier_invitation_gen2')({ invitationId })
  }

  /**
   * Fetches data from a remote service using the given SIRET number.
   * @param organization - The organization
   * @param subContractor - The subcontractor
   * @param address - The address
   * @param address.address - Street
   * @param address.zip - Zip
   * @param address.city - City
   * @param address.country - Country
   * @param invitation - The invitation data
   * @param invitation.emails - Emails to send the invitation to, they will be admin of the created org
   * @param invitation.from - Who send the invitation
   * @param invitation.from.organizationId - The organization id
   * @param invitation.from.userId - The user id
   * @returns A promise that resolves with the result containing the SIRET data.
   */
  createForSupplier(
    organization: Organization,
    subContractor: Subcontractor,
    address: {address: string, zip: string, city: string, country: string},
    invitation: { emails: string[], from: {organizationId: string, userId: string} })
    : Promise<HttpsCallableResult<void>> {
    return httpsCallable<{
      organization: Organization,
      subContractor: Subcontractor,
      address: {address: string, zip: string, city: string, country: string},
      invitation: { emails: string[], from: {organizationId: string, userId: string} }
    }, void>(
      this.functions, 'create_for_supplier_gen2')({ organization, subContractor, address, invitation })
  }

  hasPlugin(plugin: PluginEnum): boolean {
    return this.currentOrganization.plugins.includes(plugin);
  }
}
